import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router";

import {
  DeleteOutlineOutlinedIcon,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
} from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/Dialog";
import IconButton from "components/IconButton";
import { LinearProgress } from "components/Loading";
import Paper from "components/Paper";
import { EMPTY_VALUE_REGEX } from "components/RichTextEditor/constants";
import SanitizeHtml from "components/SanitizeHtml";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import { ERiskMoveDirection } from "entities/Memo/components/Sections/RisksAndMitigants/constants";
import {
  IMemoRisk,
  riskDelete,
  riskMove,
  useMemoDealDetails,
} from "entities/Memo/sdk";

const RiskReorderDialog = ({
  open,
  memoSectionId,
  onUpdate,
  risks,
  onClose,
  sectionTitle,
}: {
  open: boolean;
  onUpdate: () => void;
  memoSectionId: number;
  risks: Array<IMemoRisk>;
  onClose: () => void;
  sectionTitle: string;
}) => {
  const params = useParams<{ dealId: string; memoId: string }>();
  const dealId = Number(params.dealId);
  const memoId = Number(params.memoId);

  const { show: showConfirmationDialog } = useConfirmationDialog();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { isValidating: isFetchingMemo } = useMemoDealDetails({
    dealId,
    memoId,
  });

  const showLoading = useMemo(
    () => isSubmitting || isFetchingMemo,
    [isSubmitting, isFetchingMemo]
  );

  const onRiskDeleteClick = useCallback(
    ({ riskId }: { riskId: number }) => {
      showConfirmationDialog({
        onConfirm: () => {
          setIsSubmitting(true);
          riskDelete({
            riskId: riskId,
            memoSectionId,
            dealId,
          })
            .then(() => {
              onUpdate();
              toast.successMessage("Your item has been deleted successfully!");
            })
            .catch(handleInvalidRequest)
            .finally(() => setIsSubmitting(false));
        },
        confirmButtonText: "Delete",
        message: "Are you sure you want to delete this item?",
      });
    },
    [dealId, onUpdate, showConfirmationDialog, memoSectionId, setIsSubmitting]
  );

  const onRiskMove = useCallback(
    ({
      riskId,
      direction,
    }: {
      riskId: number;
      direction: ERiskMoveDirection;
    }) => {
      setIsSubmitting(true);
      riskMove({ riskId: riskId, memoSectionId, dealId, direction })
        .then(() => {
          onUpdate();
          toast.successMessage("Your items have been reordered successfully!");
        })
        .catch(handleInvalidRequest)
        .finally(() => setIsSubmitting(false));
    },
    [dealId, memoSectionId, onUpdate, setIsSubmitting]
  );

  return (
    <Dialog onClose={onClose} open={open} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ padding: 2 }}>
        <Paper
          sx={{
            padding: theme.spacing(2, 3),
            backgroundColor: colors.blue10,
            borderRadius: "4px",
          }}
        >
          <Text variant="labelSmall" color={colors.blue100} fontWeight={500}>
            {sectionTitle}
          </Text>
          <Text variant="h2">Manage & reorder</Text>
        </Paper>
      </DialogTitle>
      <LinearProgress
        sx={{
          position: "sticky",
          left: 0,
          visibility: showLoading ? "visible" : "hidden",
        }}
      />
      <DialogContent sx={{ padding: 2 }}>
        <Stack gap={1}>
          {risks.map((risk) => (
            <Stack
              sx={{
                padding: theme.spacing(2, 3),
                backgroundColor: colors.blue10,
                borderRadius: "4px",
              }}
              gap={1}
              key={risk.id}
            >
              <Stack direction="row" justifyContent="space-between">
                <Text variant="text2" fontWeight={700}>
                  {risk.title}
                </Text>
                <Stack direction="row">
                  <Tooltip title="Move up">
                    <IconButton disabled={showLoading}>
                      <KeyboardArrowUpOutlined
                        sx={{
                          cursor: "pointer",
                          width: "24px",
                          height: "24px",
                        }}
                        onClick={() =>
                          onRiskMove({
                            riskId: risk.id,
                            direction: ERiskMoveDirection.UP,
                          })
                        }
                      />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <IconButton disabled={showLoading}>
                      <KeyboardArrowDownOutlined
                        sx={{
                          cursor: "pointer",
                          width: "24px",
                          height: "24px",
                        }}
                        onClick={() =>
                          onRiskMove({
                            riskId: risk.id,
                            direction: ERiskMoveDirection.DOWN,
                          })
                        }
                      />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton disabled={showLoading}>
                      <DeleteOutlineOutlinedIcon
                        sx={{
                          cursor: "pointer",
                          width: "24px",
                          height: "24px",
                        }}
                        onClick={() => onRiskDeleteClick({ riskId: risk.id })}
                      />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
              {EMPTY_VALUE_REGEX.test(risk.description) ||
              risk.description === "" ? (
                <Text
                  sx={{
                    color: colors.gray60,
                    padding: theme.spacing(0, 2),
                  }}
                >
                  No content yet..
                </Text>
              ) : (
                <Stack
                  sx={{
                    maxHeight: "70px",
                    overflow: "hidden",
                    position: "relative",
                    padding: theme.spacing(0, 2),
                  }}
                >
                  <SanitizeHtml html={risk.description} />

                  <Stack
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: "3rem",
                      background: `linear-gradient(to bottom, transparent, ${colors.blue10})`,
                    }}
                  />
                </Stack>
              )}
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          padding: 2,
        }}
      >
        <Button variant="text" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RiskReorderDialog;
